import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    Alert,
    InputAdornment,
    IconButton,
    Divider,
    Stack,
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    Person,
    Lock,
    Email,
    School,
    Business,
    Group,
    PhoneIphone,
} from '@mui/icons-material';
import PageContainer from '../components/layout/PageContainer';
import { register } from '../services/authService';

const Register: React.FC = () => {
    const [formData, setFormData] = useState({
        full_name: '',
        username: '',
        password: '',
        email: '',
        university: '',
        endowment_name: '',
        activism_organization: '',
        signal_id: '',
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [usernameError, setUsernameError] = useState('');
    const navigate = useNavigate();

    const validateUsername = (username: string): string => {
        if (!username) {
            return 'Username is required';
        }
        if (username.length < 3) {
            return 'Username must be at least 3 characters long';
        }
        if (username.length > 30) {
            return 'Username must be no more than 30 characters long';
        }
        if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
            return 'Username can only contain letters, numbers, underscores, and hyphens';
        }
        if (!/^[a-zA-Z]/.test(username)) {
            return 'Username must start with a letter';
        }
        if (/^[_-]|[_-]$/.test(username)) {
            return 'Username cannot start or end with underscore or hyphen';
        }
        if (/[_-]{2,}/.test(username)) {
            return 'Username cannot contain consecutive underscores or hyphens';
        }
        return '';
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Real-time username validation
        if (name === 'username') {
            const error = validateUsername(value);
            setUsernameError(error);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setSuccess('');

        // Basic validation
        if (!formData.full_name || !formData.username || !formData.password || !formData.email) {
            setError('Please fill in all required fields.');
            setIsLoading(false);
            return;
        }

        // Username validation
        const usernameValidationError = validateUsername(formData.username);
        if (usernameValidationError) {
            setError(usernameValidationError);
            setUsernameError(usernameValidationError);
            setIsLoading(false);
            return;
        }

        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters long.');
            setIsLoading(false);
            return;
        }

        try {
            const response = await register(formData);
            setSuccess(response.message);
            
            // Clear form
            setFormData({
                full_name: '',
                username: '',
                password: '',
                email: '',
                university: '',
                endowment_name: '',
                activism_organization: '',
                signal_id: '',
            });

            // Redirect to login after 3 seconds
            setTimeout(() => {
                navigate('/login', { 
                    state: { 
                        message: 'Registration successful! Your account is pending admin approval.' 
                    } 
                });
            }, 3000);

        } catch (err: any) {
            setError(err.message || 'Registration failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    return (
        <PageContainer>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Card sx={{ maxWidth: 600, width: '100%' }}>
                    <CardContent sx={{ p: 4 }}>
                        {/* Header */}
                        <Typography
                            component="h1"
                            variant="h4"
                            sx={{
                                textAlign: 'center',
                                mb: 1,
                                fontWeight: 'bold',
                            }}
                        >
                            Register
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ textAlign: 'center', mb: 3 }}
                        >
                            Create an account to access the EthicsVC platform
                        </Typography>

                        {/* Success Message */}
                        {success && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                {success}
                            </Alert>
                        )}

                        {/* Registration Form */}
                        <Box component="form" onSubmit={handleSubmit}>
                            <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                                Required Information
                            </Typography>
                            
                            <Stack spacing={2}>
                                {/* Full Name */}
                                <TextField
                                    required
                                    fullWidth
                                    name="full_name"
                                    label="Full Name"
                                    value={formData.full_name}
                                    onChange={handleChange}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Person color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                {/* Username and Email Row */}
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        required
                                        fullWidth
                                        name="username"
                                        label="Username"
                                        value={formData.username}
                                        onChange={handleChange}
                                        error={!!usernameError}
                                        helperText={usernameError || 'Letters, numbers, underscore, hyphen allowed. Must start with letter.'}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Person color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField
                                        required
                                        fullWidth
                                        name="email"
                                        label="Email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Email color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>

                                {/* Password */}
                                <TextField
                                    required
                                    fullWidth
                                    name="password"
                                    label="Password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={formData.password}
                                    onChange={handleChange}
                                    helperText="Minimum 6 characters"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock color="action" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={handleClickShowPassword}
                                                    edge="end"
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Stack>

                            <Divider sx={{ my: 3 }} />

                            <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                                Optional Information
                            </Typography>

                            <Stack spacing={2}>
                                {/* University and Endowment Row */}
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        fullWidth
                                        name="university"
                                        label="University"
                                        value={formData.university}
                                        onChange={handleChange}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <School color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        name="endowment_name"
                                        label="Endowment Name"
                                        value={formData.endowment_name}
                                        onChange={handleChange}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Business color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>

                                {/* Activism Organization and Signal ID Row */}
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        fullWidth
                                        name="activism_organization"
                                        label="Activism Organization"
                                        value={formData.activism_organization}
                                        onChange={handleChange}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Group color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        name="signal_id"
                                        label="Signal ID"
                                        value={formData.signal_id}
                                        onChange={handleChange}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PhoneIphone color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>
                            </Stack>

                            {/* Error Alert */}
                            {error && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            {/* Submit Button */}
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={isLoading}
                                sx={{ mt: 3, mb: 2 }}
                            >
                                {isLoading ? 'Registering...' : 'Register'}
                            </Button>

                            {/* Login Link */}
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="body2">
                                    Already have an account?{' '}
                                    <Link to="/login" style={{ color: '#1976d2', textDecoration: 'none' }}>
                                        Sign in here
                                    </Link>
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </PageContainer>
    );
};

export default Register;